// Look of the whole web app: calm green on a warm grey page, white surfaces with soft borders,
// rounded corners and sentence-case buttons. It started as the household planner's theme.
// The dark variant keeps the same green on dark green-grey surfaces ("Tema" in "A minha conta").
import { alpha, createTheme, PaletteMode, Theme } from '@mui/material/styles';
import { ptPT } from '@mui/material/locale';

const lightPrimary = '#2f6b5a';
const darkPrimary = '#6fb39d';

/** Height of the top app bar; the section sidebars stick right under it. */
export const APP_BAR_HEIGHT = 56;

export function createAppTheme(mode: PaletteMode): Theme {
  const dark = mode === 'dark';
  const primary = dark ? darkPrimary : lightPrimary;
  const border = dark ? 'rgba(220, 235, 228, 0.12)' : 'rgba(28, 46, 40, 0.10)';
  const paper = dark ? '#1b2421' : '#ffffff';
  const textSecondary = dark ? '#a3b2ac' : '#5f6d68';
  return createTheme({
    palette: {
      mode,
      primary: dark
        ? { main: darkPrimary, light: '#93c9b7', dark: '#2f6b5a', contrastText: '#0f1a16' }
        : { main: lightPrimary, light: '#4f8f7c', dark: '#1f4c3f', contrastText: '#fff' },
      secondary: { main: '#d9822b' },
      warning: { main: '#d9822b', light: '#f2b56b', dark: '#a85a12' },
      background: dark ? { default: '#121917', paper } : { default: '#f4f5f2', paper },
      text: dark ? { primary: '#e4ece8', secondary: textSecondary } : { primary: '#1c2b26', secondary: textSecondary },
      divider: border,
      action: { hover: alpha(primary, dark ? 0.10 : 0.05), selected: alpha(primary, dark ? 0.18 : 0.10) },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Segoe UI Variable Text", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
      // Page titles stay modest so a laptop screen keeps room for the content under them
      h4: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.65rem', '@media (max-width:600px)': { fontSize: '1.4rem' } },
      h5: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.3rem' },
      h6: { fontWeight: 650, fontSize: '1.1rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
      overline: { fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.7rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { overflowX: 'hidden' } },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: { borderColor: border },
          rounded: { borderRadius: 14 },
          // Flat surfaces with a border instead of the default drop shadow
          elevation1: { boxShadow: 'none', border: `1px solid ${border}` },
          elevation2: { boxShadow: 'none', border: `1px solid ${border}` },
          elevation3: { boxShadow: '0 2px 10px rgba(20, 35, 30, 0.06)', border: `1px solid ${border}` },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { borderRadius: 14, borderColor: border } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 10, paddingInline: 14 } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none', fontWeight: 600, paddingInline: 12,
            '&.Mui-selected': { backgroundColor: alpha(primary, 0.12), color: primary },
          },
        },
      },
      MuiToggleButtonGroup: { styleOverrides: { root: { backgroundColor: paper } } },
      MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiTableCell: { styleOverrides: { head: { fontWeight: 650, color: textSecondary } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700 } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 12, border: `1px solid ${border}`, boxShadow: '0 8px 24px rgba(20, 35, 30, 0.12)' } } },
      MuiOutlinedInput: { styleOverrides: { root: { backgroundColor: paper } } },
      MuiAccordion: { styleOverrides: { root: { borderRadius: 14, '&:before': { display: 'none' } } } },
      MuiFab: { styleOverrides: { root: { boxShadow: '0 6px 18px rgba(31, 76, 63, 0.35)' } } },
      MuiTooltip: { defaultProps: { arrow: true } },
    },
    // Portuguese built-in texts: the Alert close button ("Fechar"), pagination, Autocomplete
  }, ptPT);
}

